import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { PatientNoteService } from '../../../core/services/patient-note.service';
import { BaseComponent } from '../../../core/components/base/base.component';
import { FeedbackService } from '../../../core/services/feedback.service';
import { MatDialog } from '@angular/material/dialog';
import { GroupService } from '../../../core/services/group.service';
import { takeUntil } from 'rxjs';
import * as moment from 'moment';
import * as _ from 'lodash';
import { checkIfGroupIsSurgical } from 'src/app/core/data/app.data';

export interface RestoreTimelineDialogData {
  patientId: string;
}

@Component({
  selector: 'app-restore-timeline-dialog',
  templateUrl: './restore-timeline-dialog.component.html',
  styleUrls: ['./restore-timeline-dialog.component.scss']
})
export class RestoreTimelineDialogComponent extends BaseComponent implements OnInit {
  deletedItems: any[] = [];
  groupedItems: any[] = [];
  loading: boolean = false;
  restoringIds: Set<string> = new Set();
  isSurgicalSpeciality: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<RestoreTimelineDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: RestoreTimelineDialogData,
    private patientNoteService: PatientNoteService,
    private groupService: GroupService,
    fs: FeedbackService,
    md: MatDialog
  ) {
    super(fs, md);
    
    this.groupService.selectedGroup$
      .pipe(takeUntil(this.destroy$))
      .subscribe(selectedGroup => {
        if (selectedGroup) {
          this.isSurgicalSpeciality = checkIfGroupIsSurgical(selectedGroup.groupSpeciality);
        }
      });
  }

  ngOnInit(): void {
    this.loadDeletedItems();
  }

  loadDeletedItems(): void {
    this.loading = true;
    this.patientNoteService.loadDeletedTimelineItems(this.data.patientId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.deletedItems = response.data || [];
          this.groupItems();
          this.loading = false;
        },
        error: (error) => {
          console.error('Error loading deleted items:', error);
          this.showToastMessage('Error loading deleted timeline items. Please try again.');
          this.loading = false;
        }
      });
  }

  groupItems(): void {
    if (!this.deletedItems || this.deletedItems.length === 0) {
      this.groupedItems = [];
      return;
    }

    // First, sort all items by most recent first (by createdAt or date, descending)
    const sortedItems = _.orderBy(this.deletedItems, item => {
      const dateTime = item.createdAt || item.date;
      return moment(dateTime).valueOf(); // Use timestamp for accurate sorting
    }, 'desc');

    // Group items by date while maintaining the sorted order
    const grouped = _.groupBy(sortedItems, item => {
      const date = item.date || item.createdAt;
      return moment(date).format('YYYY-MM-DD');
    });

    // Transform into array and sort groups by date descending (most recent dates first)
    this.groupedItems = _.chain(grouped)
      .map((value, key) => ({
        date: key,
        items: value // Items are already sorted from the initial sort
      }))
      .orderBy(group => group.date, 'desc') // Sort groups by date descending
      .value();
  }

  restoreItem(item: any): void {
    if (this.restoringIds.has(item.id)) {
      return; // Already restoring
    }

    this.restoringIds.add(item.id);
    this.patientNoteService.restoreTimelineItem(item.id, item.type)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.showToastMessage('Timeline item restored successfully');
          // Remove from list and regroup
          this.deletedItems = this.deletedItems.filter(i => i.id !== item.id);
          this.groupItems();
          this.restoringIds.delete(item.id);
        },
        error: (error) => {
          console.error('Error restoring item:', error);
          this.showToastMessage('Error restoring timeline item. Please try again.');
          this.restoringIds.delete(item.id);
        }
      });
  }


  close(): void {
    this.dialogRef.close();
  }
}

